import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router';
import _ from 'lodash';

import { Row, Col, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { RootState } from '../../../application/store';
import { Product, ProductAestheticMotif } from '../../../api-client';
import { BUSINESS_UNIT_TYPES } from '../../../utils/constants/business-unit-types';
import { isSameGender } from '../../../utils/gender-utils';
import { JEWERLY_REFINEMENT, WATCHES_REFINEMENT, GLOBAL_SEARCH_REFINEMENT } from '../../../utils/constants/refinements-definition';
import { groupProductsByAestheticLine } from '../../../utils/data-format-enchanters/order-products-enchanter';
import { keepProductsRefinements } from '../../../slices/System/systemSlice';
import { useProducts } from './hooks/useProducts';

import './products-page.scss';

import BVProductsTemplate from '../../../common/templates/BVProductsTemplate/bv-products-template';
import BVEmpty from '../../../common/components/BVEmpty/bv-empty';
import BVShimmer from '../../../common/components/BVShimmers/BVShimmer/bv-shimmer';
import BVAestheticNav from './components/BVAestheticNav/bv-aesthetic-nav';
import BVRefinementNav from '../../../common/components/BVRefinementNav/bv-refinement-nav';
import BVProductCard from './components/BVProductCard/bv-product-card';

interface IRouteParams {
    businessUnitCode: string
}

const ProductsPage: React.FunctionComponent<RouteComponentProps> = (props) => {
    const {
        handleAestheticMotifs,
        aestheticMotifs,
        aestheticMotifsLoading,
        handleProducts,
        products,
        productsLoading
    } = useProducts();

    const {
        businessUnitCode
    } = useParams<IRouteParams>();

    const _handleRefinementsSettings = () => {
        switch (businessUnitCode) {
            case BUSINESS_UNIT_TYPES.JEWELRY_CODE: {
                return JEWERLY_REFINEMENT;
            }
            case BUSINESS_UNIT_TYPES.WATCH_CODE: {
                return WATCHES_REFINEMENT;
            }

            default: {
                return [];
            }
        }
    }

    const _refinementsSettings = _handleRefinementsSettings();

    const bvCustomer = useSelector((state: RootState) => state.customer);

    const _globalSearchRef = React.useRef<any>(null);

    const [globalSearch, setGlobalSearch] = useState<any>({ display: false });

    const _handleGlobalSearch = (event: any) => {
        let charCode = (event.which) ? event.which : event.keyCode;
        if (charCode === 27) {
            setGlobalSearch({ display: false, BV_GLOBAL_SEARCH_Search: '' });
        }

        if (charCode === 13) {
            if (!!globalSearch.BV_GLOBAL_SEARCH_Search) {
                handleProducts(bvCustomer.id, {
                    businessUnitCode: businessUnitCode,
                    search: globalSearch.BV_GLOBAL_SEARCH_Search
                });
            }
        }

        if ((charCode > 47 && charCode < 58) || (charCode > 64 && charCode < 91) || (charCode > 95 && charCode < 106)) {
            if (!globalSearch.display && !globalSearch.BV_GLOBAL_SEARCH_Search) {
                setGlobalSearch({ display: true });

                _globalSearchRef.current!.focus({ cursor: 'end' });
            }
        }
    }

    useEffect(() => {
        handleAestheticMotifs(bvCustomer.id, businessUnitCode);
    }, []);

    const bvSystem = useSelector((state: RootState) => state.system);

    const [productsRefinements, setProductsRefinements] = useState<any>();

    const _handleMotifsByGender = (motifs: ProductAestheticMotif[] = [], refinements: any = {}) => {
        let motifsByGender = motifs;
        if (refinements.BV_GENDER_Gender) {
            motifsByGender = _.filter(
                motifs,
                aesthetic => _.some(aesthetic.genders, entry => isSameGender(entry, refinements.BV_GENDER_Gender)),
            );
        }

        return motifsByGender;
    }

    const _handleDefaultRefinementsValues = (motifs: ProductAestheticMotif[] = []) => {
        let _productsRefinements = {};

        if (!bvSystem.productsRefinements) {
            _.forEach(_.filter(_refinementsSettings, entry => !!entry.defaultValue), entry => {
                _productsRefinements = {
                    ..._productsRefinements,
                    [entry.key]: entry.defaultValue
                }
            });

            let motifsByGender = _handleMotifsByGender(motifs, _productsRefinements);
            if (motifsByGender.length > 0) {
                _productsRefinements = {
                    ..._productsRefinements,
                    aestheticMotif: motifsByGender[0].code,
                    aestheticMotifType: motifsByGender[0].productTypes && motifsByGender[0].productTypes[0]
                };
            }
        }
        else {
            _productsRefinements = bvSystem.productsRefinements;
        }

        setProductsRefinements(_productsRefinements);
    }

    useEffect(() => {
        if (aestheticMotifs && aestheticMotifs.length > 0) {
            _handleDefaultRefinementsValues(aestheticMotifs);
        }
    }, [aestheticMotifs]);

    const dispatch = useDispatch();

    const _handleProductsByRefinements = () => {
        let motifsByGender = _handleMotifsByGender(aestheticMotifs, productsRefinements);
        if (_.some(motifsByGender, entry => entry.code === productsRefinements.aestheticMotif)) {
            handleProducts(bvCustomer.id, {
                businessUnitCode: businessUnitCode,
                aestheticMotif: productsRefinements.aestheticMotif,
                productType: productsRefinements.aestheticMotifType
            }, productsRefinements);
        }
        else {
            if (motifsByGender.length > 0) {
                setProductsRefinements({
                    ...productsRefinements,
                    aestheticMotif: motifsByGender[0].code,
                    productType: motifsByGender[0].productTypes && motifsByGender[0].productTypes[0]
                });
            }
        }
    }

    useEffect(() => {
        if (productsRefinements) {
            _handleProductsByRefinements();

            dispatch(keepProductsRefinements(productsRefinements));
        }
    }, [productsRefinements]);

    useEffect(() => {
        document.addEventListener('keydown', _handleGlobalSearch);

        if (!globalSearch.display) {
            _handleProductsByRefinements();
        }

        return () => {
            document.removeEventListener('keydown', _handleGlobalSearch);
        };
    }, [globalSearch]);

    const [currentProduct, setCurrentProduct] = useState<Product>();
    const [previousIndex, setPreviousIndex] = useState<number>();
    const [newHover, setNewHover] = useState<boolean>(false);

    const getHoveredId = (hoveredProduct: Product, productIndex: number): void => {
        if (!currentProduct){
            setPreviousIndex(productIndex);
            setCurrentProduct(hoveredProduct);
            setNewHover(!newHover);
        }

        else if (currentProduct?.id !== hoveredProduct.id){
            closePreviousPopUps(productIndex);
            setCurrentProduct(hoveredProduct);
            setNewHover(!newHover);
        }
    }

    const closePreviousPopUps = (productIndex: number): void => {
        const allProducts : NodeListOf<Element> = document.querySelectorAll('.product-card-body');

        if (allProducts.length > 0){
            let index = -1;

            for (const previousHoveredProduct of allProducts as any){ 
                index++;

                if (index === previousIndex)
                    previousHoveredProduct.style.transform = "";
            }
        }
        
        setPreviousIndex(productIndex);
    }

    //each iteration of the outer map resets the inner index making it not unique
    let totalIndex: number = -1;

    return (
        <BVProductsTemplate {...props}>
            <div className="products-container">
                <Row style={{ display: globalSearch && globalSearch.display ? 'none' : 'flex' }}>
                    <Col span={14}>
                        <BVAestheticNav
                            data={_handleMotifsByGender(aestheticMotifs, productsRefinements)}
                            dataLoading={aestheticMotifsLoading}
                            dataFilter={productsRefinements || {}}
                            onClick={(aestheticFilter) => setProductsRefinements({
                                ...productsRefinements || {},
                                ...aestheticFilter
                            })}
                        />
                    </Col>
                    <Col span={10} style={{ textAlign: 'right' }}>
                        <BVRefinementNav
                            dataFilter={_refinementsSettings}
                            dataFilterLoading={productsLoading}
                            dataFilterValue={productsRefinements || {}}
                            onChange={setProductsRefinements}
                        >
                            <Button
                                type="primary"
                                shape="circle"
                                className="refinement-nav-btn"
                                disabled={productsLoading}
                                icon={<SearchOutlined />}
                                onClick={() => {
                                    _globalSearchRef.current!.focus({ cursor: 'end' });
                                    setGlobalSearch({ display: true });
                                }}
                            />
                        </BVRefinementNav>
                    </Col>
                </Row>
                <Row style={{ display: globalSearch && globalSearch.display ? 'flex' : 'none' }}>
                    <Col span={24}>
                        <BVRefinementNav
                            dataFilter={[
                                {
                                    ...GLOBAL_SEARCH_REFINEMENT[0],
                                    ref: _globalSearchRef
                                }
                            ]}
                            dataFilterLoading={productsLoading}
                            dataFilterValue={globalSearch}
                            onChange={setGlobalSearch}
                        />
                    </Col>
                </Row>

                <BVShimmer loading={aestheticMotifsLoading || productsLoading} type={'shimmer-card'}>
                    <BVEmpty dataSource={products || []} disclaimer={'No products found for current business unit or refinements.'}>
                        <div className="bv-products-row">
                            {_.map(groupProductsByAestheticLine(products), (dataItem, dataIndex): JSX.Element =>
                                <React.Fragment>
                                    <div key={dataIndex} className="bv-products-col-full">
                                        <span className="bv-aesthetic-line">
                                            {dataItem.aestheticLine}
                                        </span>
                                    </div>

                                    {_.map(dataItem.products, (productItem, productIndex) => (
                                        <div key={productIndex} className="bv-products-col">
                                            <BVProductCard
                                                product={productItem}
                                                getHoveredId={getHoveredId}
                                                productIndex={++totalIndex}
                                                isNewHover={newHover}
                                                hoveredProduct={currentProduct}
                                            />
                                        </div>
                                    ))}
                                </React.Fragment>
                            )}
                        </div>
                    </BVEmpty>
                </BVShimmer>
            </div>
        </BVProductsTemplate>
    );
}

export default ProductsPage;
